import * as XLSX from "xlsx";
import { convertFromTimeStampTToDate } from "../../../helpers/TimeConvert";

// A workshop with no fee and no priced answers (has_priced_options, V103)
// has nothing to pay, so its attendees are "Free" rather than "Not paid",
// and the Event List shows no paid/unpaid counts.
export const isFreeEvent = (row) =>
  !(Number(row.fee) > 0) && !(Number(row.has_priced_options) === 1);

// "Paid" | "Not paid" | "Free" for one attendee of `row`. With priced answers
// (V103) attendee-details gives each attendee's participant_fee: "Free"
// exactly when it is 0. From an older server, every attendee of a free event
// is "Free", even a $0 website checkout attendee-details marks paid, so one
// event never shows a mix, except one who paid an amount anyway.
export const attendeePaymentStatus = (row, attendee) => {
  const free =
    attendee.participant_fee != null
      ? !(Number(attendee.participant_fee) > 0)
      : isFreeEvent(row) && !(attendee.paid && Number(attendee.amount_paid) > 0);
  return free ? "Free" : attendee.paid ? "Paid" : "Not paid";
};

export const formatPaymentMethod = (attendee) =>
  [attendee.payment_method, attendee.payment_source].filter(Boolean).join(" / ");

const HEADERS = [
  "Student Name",
  "Student ID",
  "Parent Name",
  "Parent Phone",
  "Parent Email",
  "Payment Status",
  "Amount Paid",
  "Payment Method / Source",
];
const AMOUNT_COLUMN = 6;

// The event's registration questions, one column each after the standard
// ones, in the order staff set. attendee-details lists every field the event
// has had (inactive too), so older answers keep their column.
export const answerColumns = (details) =>
  [...(details?.fields || [])].sort(
    (a, b) => (Number(a.order_num) || 0) - (Number(b.order_num) || 0)
  );

// An attendee's answer to one question ("" when unanswered); a multi-choice
// answer comes from the server already joined with ", ".
export const attendeeAnswer = (attendee, field) => attendee.answers?.[field.field_key] ?? "";

// "2026-10-12" or "2026-10-12T04:00:00.000Z" -> a local-midnight Date, so
// Excel shows the same calendar day the portal does.
const toDate = (value) => {
  if (!value) return null;
  const [y, m, d] = convertFromTimeStampTToDate(String(value)).split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

// The event's details, once, above the attendee table: its name, then
// label / value rows (the dates as real date cells).
export function buildEventHeading(row) {
  return [
    [row.workshop_name],
    ["Event ID", row.workshop_id],
    ["Start Date", toDate(row.startdate)],
    ["End Date", toDate(row.enddate)],
  ];
}

// The attendee table: a header row, then one row per attendee. A pending
// website checkout has no portal student id yet, so it says so instead.
export function buildAttendeeRows(row, details) {
  const questions = answerColumns(details);
  const attendeeCells = (attendee, studentId) => {
    const status = attendeePaymentStatus(row, attendee);
    const paid = status === "Paid";
    return [
      attendee.student_name,
      studentId,
      attendee.parent_name || "",
      attendee.parent_phone || "",
      attendee.parent_email || "",
      status,
      paid && attendee.amount_paid != null ? Number(attendee.amount_paid) : null,
      paid ? formatPaymentMethod(attendee) : "",
      ...questions.map((field) => attendeeAnswer(attendee, field)),
    ];
  };

  return [
    [...HEADERS, ...questions.map((field) => field.label)],
    ...(details?.confirmed || []).map((a) => attendeeCells(a, a.id)),
    ...(details?.pending || []).map((a) => attendeeCells(a, "Pending web transaction")),
  ];
}

// Keeps the event name as typed (accents, other scripts) and replaces only
// what a filename can't hold, plus whitespace; an empty name is left out.
export function attendeesExportFilename(row) {
  const name = (row.workshop_name || "")
    .replace(/[\\/:*?"<>|\s]+/g, "_")
    .replace(/^[_.]+|[_.]+$/g, "");
  return `Event_${row.workshop_id}${name ? `_${name}` : ""}_attendees.xlsx`;
}

// Builds and downloads the .xlsx. Text cells are never evaluated as formulas,
// so names and emails from the website form need no escaping.
export function downloadAttendeesXlsx(row, details) {
  const heading = buildEventHeading(row);
  const rows = buildAttendeeRows(row, details);
  const headers = rows[0];
  // A blank row between the heading and the table.
  const firstTableRow = heading.length + 1;
  const sheet = XLSX.utils.aoa_to_sheet([...heading, [], ...rows], { dateNF: "yyyy-mm-dd" });

  // The event name spans the table's width.
  sheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];

  // Real number cells for the amount, shown as currency.
  for (let r = 1; r < rows.length; r++) {
    const cell = sheet[XLSX.utils.encode_cell({ r: firstTableRow + r, c: AMOUNT_COLUMN })];
    if (cell && cell.t === "n") cell.z = "$#,##0.00";
  }
  // Size each column to its longest table value; the heading's labels and
  // dates (10 characters) must fit too, the merged event name need not.
  sheet["!cols"] = headers.map((header, c) => ({
    wch: Math.min(
      50,
      Math.max(
        ...rows.map((cells) => String(cells[c] ?? "").length),
        ...heading.slice(1).map((cells) => (c === 1 ? 10 : String(cells[c] ?? "").length))
      ) + 2
    ),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Attendees");
  XLSX.writeFile(workbook, attendeesExportFilename(row));
}
